import { LOCATION_CHANGED } from '../ducks/routing.duck';

/**
 * Gets or creates a unique device ID stored in localStorage.
 * @returns {string} A unique device identifier
 */
export const getDeviceId = () => {
  let deviceId = typeof window !== 'undefined' ? localStorage.getItem('deviceId') : null;

  if (!deviceId && typeof window !== 'undefined') {
    deviceId = crypto.randomUUID();
    localStorage.setItem('deviceId', deviceId);
  }

  return deviceId;
};

export const getSessionId = () => {
  if (typeof window === "undefined") return null;

  let sessionId = sessionStorage.getItem("sessionId");

  if (!sessionId) {
    const tempSessionId = localStorage.getItem("tempSessionId");

    if (tempSessionId) {
      sessionId = tempSessionId;
      sessionStorage.setItem("sessionId", sessionId);
      localStorage.removeItem("tempSessionId");
    }
  }

  if (!sessionId) {
    sessionId = crypto.randomUUID();
    sessionStorage.setItem("sessionId", sessionId);
    localStorage.setItem("tempSessionId", sessionId);
  }

  return sessionId;
};

export const setSessionUser = ({ userId, userType, userEmail }) => {
  if (typeof window === "undefined") return;

  if (userId) {
    // Set user data when logged in
    sessionStorage.setItem("userId", userId);
    if (userType) sessionStorage.setItem("userType", userType);
    if (userEmail) sessionStorage.setItem("userEmail", userEmail);
    localStorage.setItem("tempUser", JSON.stringify({ userId, userType, userEmail }));
  } else {
    // Clear user data on logout - keep deviceId, clear user identity
    sessionStorage.removeItem("userId");
    sessionStorage.removeItem("userType");
    sessionStorage.removeItem("userEmail");
    localStorage.removeItem("tempUser");
  }
};

export const getSessionUser = () => {
  if (typeof window === "undefined") {
    return { userId: null, userType: null, userEmail: null };
  }

  const userId = sessionStorage.getItem("userId");

  // No userId in sessionStorage = logged out or guest
  // Do NOT fall back to tempUser - that causes identity leakage
  if (!userId) {
    return { userId: null, userType: null, userEmail: null };
  }

  return {
    userId,
    userType: sessionStorage.getItem("userType") || null,
    userEmail: sessionStorage.getItem("userEmail") || null
  };
};


export const initializeSession = () => {
  if (typeof window === 'undefined') {
    return null;
  }
  if (window.__sessionInitialized) {
    return getSessionId();
  }
  window.__sessionInitialized = true;
  const sessionId = getSessionId();
  getSessionUser();
  const cleanup = () => {
    localStorage.removeItem("tempSessionId");
    localStorage.removeItem("tempUser");
  };
  window.addEventListener('beforeunload', cleanup);
  window.addEventListener('pagehide', cleanup);
  return sessionId;
};

// Create a Redux middleware from the given analytics handlers. Each
// handler should have the following methods:
//
// - trackPageView(canonicalPath, previousPath): called when the URL is changed
export const createMiddleware = handlers => store => next => action => {
  const { type, payload } = action;

  if (type === LOCATION_CHANGED) {
    const previousPath = store?.getState()?.routing?.currentCanonicalPath;
    const { canonicalPath } = payload;
    handlers.forEach(handler => {
      handler.trackPageView(canonicalPath, previousPath);
    });
  }

  next(action);
};
